import { m } from 'framer-motion';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
// routes
import { useRouter } from 'src/routes/hooks';
// components
import { varHover } from 'src/components/animate';
import { useSnackbar } from 'src/components/snackbar';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { useCurrentUserQuery, useSignoutMutation } from 'src/context/api/auth';
import { useAuth0 } from '@auth0/auth0-react';
import mixpanel from 'mixpanel-browser';
import { useAuth } from 'src/auth';
import { Switch } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { setDemoMode } from 'src/context/reducers/filter-settings/filterSlice';
import BaseOptions from 'src/components/settings/drawer/base-option';
import { useSettingsContext } from 'src/components/settings';
import LanguagePopover from './language-popover';

// ----------------------------------------------------------------------

const OPTIONS = [
  {
    label: 'Profile',
    linkTo: '/account',
  },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const router = useRouter();
  const { user }: any = useAuth();
  const { isAuthenticated, logout } = useAuth0();
  const [signout] = useSignoutMutation();
  const { enqueueSnackbar } = useSnackbar();
  const popover = usePopover();
  const dispatch = useDispatch();
  const demoMode = useSelector((state: any) => state.filters.demoMode);
  const usr: any = useCurrentUserQuery();
  const role = usr?.data?.role;

  const handleLogout = async () => {
    try {
      await signout({});
      if (isAuthenticated) await logout();
      mixpanel.track('Log Out Success', { email: user?.email });

      popover.onClose();
      router.reload();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const handleClickItem = (path: string) => {
    popover.onClose();
    router.push(path);
  };

  const theme = useTheme();
  const settings = useSettingsContext();

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          ml: 1,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          // ...(popover.open && {
          //   background: (theme) =>
          //     `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          // }),
        }}
      >
        <Box
          sx={{
            width: 37,
            height: 37,
            flexShrink: 0,
            borderRadius: '50%',
            position: 'relative',
            alignItems: 'center',
            display: 'inline-flex',
            justifyContent: 'center',
            backgroundColor: theme.palette.primary.main,
          }}
        >
          <Typography sx={{ fontSize: '1rem', color: theme.palette.common.white }}>
            {user?.profile?.firstname?.charAt(0).toUpperCase()}
            {user?.profile?.lastname?.charAt(0).toUpperCase()}
          </Typography>

          <Box
            component={m.span}
            animate={{ rotate: 360 }}
            transition={{
              duration: 8,
              ease: 'linear',
              repeat: Infinity,
            }}
            sx={{
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              position: 'absolute',
              borderRadius: 'inherit',
              // background: 'conic-gradient(#D92EF2, #8623F5, #80DFFB, #8623F4)',
              // mask: 'linear-gradient(#FFF 0 0) content-box, linear-gradient(#FFF 0 0)',
              // WebkitMask: 'linear-gradient(#FFF 0 0) content-box, linear-gradient(#FFF 0 0)',
              maskComposite: 'exclude',
              WebkitMaskComposite: 'xor',
              p: `3px`,
            }}
          />
        </Box>
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 200, p: 0 }}>
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.profile?.firstname} {user?.profile?.lastname}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {OPTIONS.map((option) => (
            <MenuItem key={option.label} onClick={() => handleClickItem(option.linkTo)}>
              {option.label}
            </MenuItem>
          ))}
        </Stack>


        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1, display: 'flex', justifyContent: 'space-between' }}>
          <MenuItem
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1
            }}
          >
            Language

            <LanguagePopover />

          </MenuItem>
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1, display: 'flex', justifyContent: 'space-between' }}>
          <MenuItem
            onClick={() =>
              settings.onUpdate('themeMode', settings.themeMode === 'dark' ? 'light' : 'dark')
            }
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1
            }}
          >
            Dark Theme

            <Switch
              checked={settings.themeMode === 'dark'}
              size="small"
              onChange={(e) =>
                settings.onUpdate('themeMode', e.target.checked ? 'dark' : 'light')
              }
              inputProps={{ 'aria-label': 'Theme mode toggle' }}
            />
          </MenuItem>
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />



        {["admin", "brand specialist"].includes(role) &&
          <Stack sx={{ p: 1, display: 'flex', justifyContent: 'space-between' }}>

            <MenuItem
              onClick={() => dispatch(setDemoMode(!demoMode))}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1
              }}
            >
              Demo mode

              <Switch
                checked={demoMode}
                size='small'
                onChange={(e) => dispatch(setDemoMode(e.target.checked))}
                inputProps={{ 'aria-label': 'Demo mode toggle' }}
              />
            </MenuItem>

          </Stack>
        }

        <Divider sx={{ borderStyle: 'dashed' }} />


        <MenuItem
          onClick={handleLogout}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          Logout
        </MenuItem>
      </CustomPopover>
    </>
  );
}
